<template>
  <lf-modal
    title=""
    :close="close"
    :navigation
    @navigate="$emit('navigate', $event)"
  >
    <template #header>
      <div class="flex w-full items-center justify-between gap-4 pb-4">
        <div class="flex min-w-0 items-center gap-3">
          <p
            class="overflow-hidden text-2xl font-semibold text-ellipsis whitespace-nowrap text-gray-600"
          >
            {{ item.original_name }}
          </p>
          <div class="flex shrink-0 items-center rounded">
            <button
              v-tooltip="$t('DEALS.DOCUMENTS.DOCUMENT_PREVIEW')"
              class="flex h-10 w-10 items-center justify-center rounded-l transition-colors"
              :class="
                activeTab === 'document'
                  ? 'bg-surface-user-blue text-primary'
                  : 'border border-divider text-label hover:text-gray-600'
              "
              data-cy="tab-document"
              @click="activeTab = 'document'"
            >
              <icon-base
                :icon="IconDocumentPreview"
                width="16"
                height="16"
                view-box="0 0 16 16"
              />
            </button>
            <button
              v-tooltip="$t('DEALS.DOCUMENTS.OPEN_METADATA')"
              class="flex h-10 w-10 items-center justify-center rounded-r transition-colors"
              :class="
                activeTab === 'metadata'
                  ? 'bg-surface-user-blue text-primary'
                  : 'border border-l-0 border-divider text-label hover:text-gray-600'
              "
              data-cy="tab-metadata"
              @click="activeTab = 'metadata'"
            >
              <icon-base :icon="IconCurlyBraces" />
            </button>
          </div>
        </div>
        <div class="flex shrink-0 items-center gap-2">
          <template v-if="activeTab === 'document'">
            <icon-base
              class="cursor-pointer"
              :icon="IconDownloadLarge"
              width="40"
              height="40"
              data-cy="document-download-btn"
              @click="download"
            />
          </template>
          <template v-else>
            <button
              v-tooltip="$t('COMMON.EDIT')"
              class="flex h-10 w-10 cursor-pointer items-center justify-center rounded border border-divider bg-white text-gray-500 hover:text-gray-700"
              :class="{ 'bg-primary/10 text-primary': formMode === 'editing' }"
              data-cy="metadata-edit-btn"
              @click="startEditing"
            >
              <icon-base :icon="IconEdit" />
            </button>
            <button
              v-tooltip="$t('DEALS.DOCUMENTS.ADD_METADATA_ENTRY')"
              class="flex h-10 w-10 cursor-pointer items-center justify-center rounded border border-divider bg-white text-gray-500 hover:text-gray-700"
              data-cy="metadata-add-btn"
              @click="startAddEntry"
            >
              <icon-base
                :icon="IconPlusSmall"
                width="20"
                height="20"
                view-box="0 0 16 16"
              />
            </button>
            <button
              v-tooltip="$t('COMMON.COPY')"
              class="flex h-10 w-10 cursor-pointer items-center justify-center rounded border border-divider bg-white text-gray-500 hover:text-gray-700"
              data-cy="metadata-copy-btn"
              @click="copyAllMetadata"
            >
              <icon-base :icon="IconCopy" width="20" height="20" />
            </button>
            <icon-base
              class="cursor-pointer text-gray-500 hover:text-gray-700"
              :icon="IconDownloadLarge"
              width="40"
              height="40"
              @click="downloadMetadata"
            />
          </template>
        </div>
      </div>
    </template>
    <template #content>
      <div class="flex h-70-screen w-full items-center justify-center">
        <template v-if="activeTab === 'document'">
          <show-file v-if="item.file_url" :file-url="item.file_url" />
        </template>

        <template v-else>
          <div
            v-if="isMetadataLoading"
            class="flex h-full w-full items-center justify-center"
          >
            <loader is-loading />
          </div>

          <metadata-entry-form
            v-else-if="formMode !== 'idle'"
            :initial-key="formData.key"
            :initial-value="formData.value"
            :show-delete="formMode === 'editing' && !!selectedEntry"
            :saving="isSavePending"
            @save="saveForm"
            @cancel="formMode = 'idle'"
            @delete="showDeleteConfirm = true"
          />

          <div
            v-else-if="!metadataEntries.length"
            class="flex h-full w-full flex-col items-center justify-center gap-4 text-gray-400"
          >
            <icon-base :icon="IconCurlyBraces" width="48" height="48" />
            <p>{{ $t("DEALS.DOCUMENTS.NO_METADATA_ENTRIES") }}</p>
          </div>

          <metadata-entry-preview
            v-else
            :entries="metadataEntries"
            :selected-id="selectedMetadataId"
            @select="selectedMetadataId = $event"
          />
        </template>
      </div>
    </template>
  </lf-modal>

  <confirm-modal
    v-if="showDeleteConfirm"
    :title="t('DEALS.TRACKING.DELETE_METADATA')"
    :description="t('DEALS.DOCUMENTS.DELETE_METADATA_CONFIRM')"
    confirm-button-type="error"
    :confirm-button="t('COMMON.DELETE')"
    :cancel-button="t('COMMON.CANCEL')"
    :close="() => (showDeleteConfirm = false)"
    @confirm="deleteSelectedEntry"
  />
</template>

<script setup lang="ts">
import { onKeyStroke } from "@vueuse/core";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { downloadFile, downloadObjectAsJson } from "@/helpers/files";
import { useClipboardWithMessage } from "@/hooks/clipboard";
import {
  useDeleteGenericMetadataMutation,
  useGenericMetadataQuery,
  useSaveGenericMetadataMutation,
} from "@/hooks/queries/metadata";

import ShowFile from "@/components/ShowFile.vue";
import MetadataEntryForm from "@/components/deals/documents/MetadataEntryForm.vue";
import MetadataEntryPreview from "@/components/deals/documents/MetadataEntryPreview.vue";
import IconCopy from "@/components/icons/IconCopy.vue";
import IconCurlyBraces from "@/components/icons/IconCurlyBraces.vue";
import IconDocumentPreview from "@/components/icons/IconDocumentPreview.vue";
import IconDownloadLarge from "@/components/icons/IconDownloadLarge.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconPlusSmall from "@/components/icons/IconPlusSmall.vue";
import ConfirmModal from "@/components/modals/ConfirmModal.vue";
import LfModal from "@/components/ui/Modal.vue";

import type {
  NavigateDirection,
  NavigationConfig,
} from "@/components/ui/Modal.vue";
import type { IFile } from "@/models/common";
import type { Metadata } from "@/schemas/metadata";

const { t } = useI18n();
const copyToClipboard = useClipboardWithMessage();

const emit = defineEmits<{
  navigate: [direction: NavigateDirection];
}>();

const { item, close, navigation } = defineProps<{
  item: Partial<IFile>;
  close: () => void;
  navigation?: NavigationConfig;
}>();

const activeTab = ref<"document" | "metadata">("document");
const formMode = ref<"idle" | "adding" | "editing">("idle");
const showDeleteConfirm = ref(false);
const formData = ref({ key: "", value: "" });
const selectedMetadataId = ref<string>("");

const fileId = computed(() => item.id);

const metadataOptions = { modelType: "file" as const, modelId: fileId };

const { data: metadataData, isLoading: isMetadataLoading } =
  useGenericMetadataQuery({
    ...metadataOptions,
    enabled: computed(() => activeTab.value === "metadata" && !!fileId.value),
  });

const { isPending: isSavePending, mutateAsync: saveMutation } =
  useSaveGenericMetadataMutation(metadataOptions);

const { mutateAsync: deleteMetadata } =
  useDeleteGenericMetadataMutation(metadataOptions);

const metadataEntries = computed<Metadata[]>(() => metadataData.value ?? []);

const selectedEntry = computed(() =>
  metadataEntries.value.find(
    (e) => e.id?.toString() === selectedMetadataId.value
  )
);

watch(metadataEntries, (entries) => {
  if (!entries.length) {
    selectedMetadataId.value = "";
    return;
  }

  const currentStillExists = entries.some(
    (e) => e.id?.toString() === selectedMetadataId.value
  );
  if (!currentStillExists) {
    selectedMetadataId.value = entries[0].id?.toString() ?? "";
  }
});

const download = () => {
  const { file_url, original_name, isSignedDocument } = item;
  if (!file_url) {
    return;
  }
  return downloadFile(file_url, {}, original_name, !isSignedDocument);
};

const startAddEntry = () => {
  formData.value = { key: "", value: "" };
  formMode.value = "adding";
};

const startEditing = () => {
  const entry = selectedEntry.value;
  if (!entry) {
    return;
  }
  formData.value = { key: entry.key, value: entry.value };
  formMode.value = "editing";
};

const saveForm = async ({ key, value }: { key: string; value: string }) => {
  const payload = { key, value };

  if (formMode.value === "editing" && selectedEntry.value) {
    await saveMutation({
      mode: "update",
      metadataId: selectedEntry.value.id,
      payload,
    });
    formMode.value = "idle";
    return;
  }

  const created = await saveMutation({ mode: "create", payload });
  formMode.value = "idle";
  selectedMetadataId.value = created.id?.toString() ?? "";
};

const deleteSelectedEntry = async () => {
  const entry = selectedEntry.value;
  if (!entry) {
    return;
  }
  await deleteMetadata(entry.id);
  showDeleteConfirm.value = false;
  formMode.value = "idle";
};

const metadataAsObject = computed(() => {
  const result: Record<string, string> = {};
  for (const entry of metadataEntries.value) {
    result[entry.key] = entry.value;
  }
  return result;
});

const copyAllMetadata = () => {
  copyToClipboard(JSON.stringify(metadataAsObject.value, null, 2), {
    success: t("DEALS.DOCUMENTS.METADATA_COPIED"),
  });
};

const downloadMetadata = () => {
  downloadObjectAsJson(
    metadataAsObject.value,
    item.original_name ?? "metadata"
  );
};

const getNavigateShortcut = (direction: NavigateDirection) => () => {
  if (navigation) {
    emit("navigate", direction);
  }
};

onKeyStroke("ArrowLeft", getNavigateShortcut("previous"));
onKeyStroke("ArrowRight", getNavigateShortcut("next"));
</script>
